import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Table,
  Modal,
  Spinner,
} from 'react-bootstrap'
import { Table as SuperTable, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'
import axios from 'axios'
import Cookies from 'js-cookie'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import {
  BsFunnelFill,
  BsSearch,
  BsArrowLeft,
  BsPersonPlusFill,
  BsCheckCircleFill,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import SelectableField from './commonComponents/SelectableField'
import { useInstitute } from './commonComponents/InstituteContext'
import CertificateTemplate1 from '../certificateTemplates/CertificateTemplate1'
import CertificateTemplate2 from '../certificateTemplates/CertificateTemplate2'
import CertificateTemplate3 from '../certificateTemplates/CertificateTemplate3'
import CertificateTemplate4 from '../certificateTemplates/CertificateTemplate4'
import CertificateTemplate5 from '../certificateTemplates/CertificateTemplate5'
import CertificateTemplate6 from '../certificateTemplates/CertificateTemplate6'
import CertificateTemplate7 from '../certificateTemplates/CertificateTemplate7'
import CertificateTemplate8 from '../certificateTemplates/CertificateTemplate8'

const templateRegistry = [
  { id: '1', title: 'Achievement/Merit Certificate', type: 'achievement_merit_template', component: CertificateTemplate1 },
  { id: '2', title: 'Transfer Certificate', type: 'transfer_certificate_template', component: CertificateTemplate2 },
  { id: '3', title: 'Character Certificate', type: 'character_certificate_template', component: CertificateTemplate3 },
  { id: '4', title: 'Vocational/Skill Certificate', type: 'vocational_skill_template', component: CertificateTemplate4 },
  { id: '5', title: 'Passing Certificate', type: 'passing_certificate_template', component: CertificateTemplate5 },
  { id: '6', title: 'Migration Certificate', type: 'migration_certificate_template', component: CertificateTemplate6 },
  { id: '7', title: 'Cultural Activity Certificate', type: 'cultural_activity_template', component: CertificateTemplate7 },
  { id: '8', title: 'Improvement Certificate', type: 'improvement_certificate_template', component: CertificateTemplate8 },
]

const getTemplateIdByType = (templateType) =>
  templateRegistry.find((tpl) => tpl.type === templateType)?.id || null

const normalizeTemplateId = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (trimmed.startsWith('template')) {
      const numericPart = Number(trimmed.replace('template', ''))
      if (!Number.isNaN(numericPart) && numericPart >= 1 && numericPart <= templateRegistry.length) {
        return String(numericPart)
      }
      return null
    }
    const numeric = Number(trimmed)
    if (!Number.isNaN(numeric)) {
      if (numeric >= 1 && numeric <= templateRegistry.length) return String(numeric)
      if (numeric >= 0 && numeric < templateRegistry.length) return String(numeric + 1)
    }
    return null
  }

  if (typeof value === 'number') {
    if (value >= 1 && value <= templateRegistry.length) return String(value)
    if (value >= 0 && value < templateRegistry.length) return String(value + 1)
  }

  return null
}

const AssignTemplateCertificate = () => {
  const { guardianId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo, name, id } = useInstitute()
  const [guardian, setGuardian] = useState(null)
  const [students, setStudents] = useState([])
  const [filteredStudents, setFilteredStudents] = useState([])
  const [selectedStudents, setSelectedStudents] = useState([])
  const [loading, setLoading] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedData, setSelectedData] = useState({})
  const [resetKey, setResetKey] = useState(0)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [showFilterCard, setShowFilterCard] = useState(true)
  const [previewTemplateId, setPreviewTemplateId] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const previewTemplate = templateRegistry.find((tpl) => tpl.id === previewTemplateId)
  const PreviewComponent = previewTemplate?.component

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  // Fetch guardian info
  useEffect(() => {
    if (guardianId) {
      // Check if guardian data was passed via navigation state
      if (location.state?.guardian) {
        console.log('Guardian from navigation state:', location.state.guardian)
        setGuardian(location.state.guardian)
      } else {
        fetchGuardianInfo()
      }
    }
  }, [guardianId, location.state])

  // Fetch all students
  useEffect(() => {
    fetchAllStudents()
  }, [])

  const fetchGuardianInfo = async () => {
    try {
      const institueId = Cookies.get('institute_id')
      const userCookie = Cookies.get('user')
      let token = null

      if (userCookie) {
        try {
          const user = JSON.parse(userCookie)
          token = user.token
        } catch (err) {
          console.error('Failed to parse user cookie', err)
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : undefined,
        'institute-Id': Number(institueId),
      }

      // Try multiple endpoint patterns
      const endpoints = [
        `/parents/${guardianId}`,
        `/guardians/${guardianId}`,
        `/parent/${guardianId}`,
      ]

      let guardianData = null
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(BASE_URL + endpoint, { headers })
          console.log('Guardian API Response:', response.data)
          
          // Handle different response structures
          if (response.data) {
            guardianData = response.data.data || response.data.parent || response.data.guardian || response.data
            if (guardianData && (guardianData.email || guardianData.id)) {
              break
            }
          }
        } catch (endpointError) {
          console.error(`Error with endpoint ${endpoint}:`, endpointError)
          continue
        }
      }

      if (guardianData) {
        console.log('Guardian data set:', guardianData)
        setGuardian(guardianData)
      } else {
        console.error('Guardian data not found in any endpoint')
        toast.error('Failed to fetch guardian information')
      }
    } catch (error) {
      console.error('Error fetching guardian info:', error)
      toast.error('Failed to fetch guardian information')
    }
  }

  const fetchAllStudents = async () => {
    setLoading(true)
    try {
      const institueId = Cookies.get('institute_id')
      const userCookie = Cookies.get('user')
      let token = null

      if (userCookie) {
        try {
          const user = JSON.parse(userCookie)
          token = user.token
        } catch (err) {
          console.error('Failed to parse user cookie', err)
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : undefined,
        'institute-Id': Number(institueId),
      }

      const response = await axios.get(BASE_URL + '/student/all', { headers })
      const allStudents = response.data.data || []
      setStudents(allStudents)
      setFilteredStudents(allStudents)
    } catch (error) {
      console.error('Error fetching students:', error)
      toast.error('Failed to fetch students')
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (selectedValues) => {
    setSelectedData(selectedValues)
    filterStudents(selectedValues)
  }

  const filterStudents = (filters) => {
    let filtered = [...students]

    if (filters.course) {
      filtered = filtered.filter(
        (s) => s.course_id === parseInt(filters.course),
      )
    }

    if (filters.class) {
      filtered = filtered.filter(
        (s) => s.class_id === parseInt(filters.class),
      )
    }

    if (filters.student) {
      const studentId = parseInt(filters.student)
      filtered = filtered.filter((s) => s.id === studentId)
    }

    // Apply search filter
    if (search.trim()) {
      filtered = filtered.filter((student) =>
        Object.values(student).some(
          (value) =>
            value !== null &&
            value !== undefined &&
            value.toString().toLowerCase().includes(search.trim().toLowerCase()),
        ),
      )
    }

    setFilteredStudents(filtered)
  }

  useEffect(() => {
    filterStudents(selectedData)
  }, [search, selectedData, students])

  const handleStudentToggle = (studentId) => {
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId)
      } else {
        return [...prev, studentId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([])
    } else {
      setSelectedStudents(filteredStudents.map((s) => s.id))
    }
  }

  const handleAssignStudents = async () => {
    if (!guardian) {
      toast.error('Guardian information not loaded. Please wait...')
      return
    }

    // Get email from guardian object - try multiple possible field names
    const guardianEmail = guardian.email || guardian.parent_email || guardian.user?.email
    
    if (!guardianEmail) {
      console.error('Guardian object:', guardian)
      toast.error('Guardian email not found. Please refresh the page.')
      return
    }

    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student')
      return
    }

    setAssigning(true)
    const userCookie = Cookies.get('user')
    let token = null

    if (userCookie) {
      try {
        const user = JSON.parse(userCookie)
        token = user.token
      } catch (err) {
        console.error('Failed to parse user cookie', err)
      }
    }

    let successCount = 0
    let failCount = 0

    for (const studentId of selectedStudents) {
      try {
        const guardianEmail = guardian.email || guardian.parent_email || guardian.user?.email
        const response = await axios.post(
          BASE_URL + '/guardian/assign/student',
          {
            email: guardianEmail,
            student_id: studentId,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        successCount++
      } catch (error) {
        failCount++
        console.error(`Failed to assign student ${studentId}:`, error)
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully assigned ${successCount} student(s)`)
    }
    if (failCount > 0) {
      toast.error(`Failed to assign ${failCount} student(s)`)
    }

    setSelectedStudents([])
    setAssigning(false)
  }

  const handleViewActivatedTemplate = async () => {
    const institueId = Cookies.get('institute_id')
    const userCookie = Cookies.get('user')
    let token = null

    if (userCookie) {
      try {
        const parsedUser = JSON.parse(userCookie)
        token = parsedUser?.token || null
      } catch {
        token = null
      }
    }

    if (!institueId || !token) {
      toast.error('Missing institute or token. Please login again.')
      return
    }

    try {
      setPreviewLoading(true)
      const response = await axios.get(`${BASE_URL}/template`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'institute-id': Number(institueId),
          'institute-Id': Number(institueId),
        },
      })

      const rawData = response?.data?.data
      const rows = Array.isArray(rawData)
        ? rawData
        : rawData && typeof rawData === 'object'
          ? [rawData]
          : Array.isArray(response?.data)
            ? response.data
            : []

      const firstRow = rows[0]

      const resolvedTemplateId =
        normalizeTemplateId(firstRow?.template_id) ||
        normalizeTemplateId(firstRow?.id) ||
        getTemplateIdByType(firstRow?.template_type)

      if (!resolvedTemplateId) {
        toast.error('No template found in response.')
        return
      }

      setPreviewTemplateId(resolvedTemplateId)
    } catch (error) {
      console.error('Error fetching activated template:', error)
      toast.error('Failed to fetch activated template')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleRefresh = () => {
    setSelectedData({})
    setSearch('')
    setSelectedStudents([])
    setResetKey((prev) => prev + 1)
    fetchAllStudents()
  }

  return (
    <>
      <style>
        {`
          .transfer-page-title {
            letter-spacing: -0.02em;
            color: #223843;
            font-family: Inter, system-ui, sans-serif;
          }

          .table td {
            user-select: text;
          }
          .table tbody tr {
            cursor: default;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              {/* Left: Back Button, Title */}
              <div className="d-flex align-items-center gap-3">

                {/* School Logo */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold transfer-page-title text-start"
                    style={{ fontSize: isMobile ? '20px' : undefined }}
                  >
                    Generate Template-Based Certificates To Student
                  </h3>
                  {guardian && (
                    <p
                      className="m-0 mt-0 text-start"
                      style={{
                        fontSize: isMobile ? '12px' : '13px',
                        color: '#6b7280',
                      }}
                     >
                       Guardian: {guardian.name || 'N/A'} ({(guardian.email || guardian.parent_email || guardian.user?.email) || 'N/A'})
                     </p>
                  )}
                </div>
              </div>

              {/* Right: Profile Icon with Dropdown */}
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          {/* Filter Toggle Button */}
          <div
            className="d-flex mb-4"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: isMobile ? 'flex-start' : 'space-between',
              gap: '8px',
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
            {selectedStudents.length > 0 && (
              <div
                className="d-flex align-items-center gap-2"
                style={{
                  marginTop: isMobile ? '8px' : '0',
                }}
              >
                <Button
                  variant="outline-primary"
                  onClick={handleViewActivatedTemplate}
                  disabled={previewLoading}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#223843'
                    e.currentTarget.style.color = '#ffffff'
                    e.currentTarget.style.borderColor = '#223843'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.color = '#223843'
                    e.currentTarget.style.borderColor = '#223843'
                  }}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    borderColor: '#223843',
                    color: '#223843',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {previewLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading Template...
                    </>
                  ) : (
                    'View Activated Template'
                  )}
                </Button>
                <Button
                  variant="success"
                  onClick={handleAssignStudents}
                  disabled={assigning}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {assigning ? (
                    'Assigning...'
                  ) : (
                    <>
                      <BsPersonPlusFill size={18} className="me-2" />
                      Assign {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Filter Section */}
          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
            }}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden',
                }}
              >
                {/* Header */}
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5
                          className="m-0 fw-semibold text-start"
                          style={{
                            color: '#111827',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Filter Students
                        </h5>
                        <p
                          className="m-0 mt-1"
                          style={{ fontSize: '13px', color: '#6b7280' }}
                        >
                          Filter students by course, class, or search by name
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsFunnelFill size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <Row className="g-3">
                    {/* SelectableField Filters */}
                    <Col xs={12}>
                      <div
                        style={{
                          padding: '20px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#223843',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <BsFunnelFill
                              style={{ color: '#ffffff', fontSize: '16px' }}
                            />
                          </div>
                          <label
                            className="fw-semibold mb-0"
                            style={{
                              color: '#111827',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Filter by Course, Class & Student
                          </label>
                        </div>
                        <SelectableField
                          onSelect={handleSelect}
                          showCourse={true}
                          showClass={true}
                          showStudent={true}
                          abc={'abc'}
                          setData={setFilteredStudents}
                          resetTrigger={resetKey}
                        />
                      </div>
                    </Col>

                    {/* Search Student Name */}
                    <Col xs={12}>
                      <div
                        style={{
                          padding: '20px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#10B981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <BsSearch
                              style={{ color: '#ffffff', fontSize: '16px' }}
                            />
                          </div>
                          <label
                            className="fw-semibold mb-0"
                            style={{
                              color: '#111827',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Search Student Name
                          </label>
                        </div>
                        <div style={{ position: 'relative' }}>
                          <BsSearch
                            style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '16px',
                              zIndex: 1,
                            }}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Enter student name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              paddingLeft: '40px',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#223843'
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#D1D3D4'
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* Students Table */}
          <Card
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: '#ffffff',
            }}
          >
            <SuperTable className="table mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <Thead>
                <Tr style={{ backgroundColor: '#F3F4F6' }}>
                  <Th
                    style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '16px',
                      border: 'none',
                      borderBottom: '1px solid #D1D3D4',
                      textAlign: 'left',
                      color: '#111827',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      width: '50px',
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={
                        filteredStudents.length > 0 &&
                        selectedStudents.length === filteredStudents.length
                      }
                      onChange={handleSelectAll}
                    />
                  </Th>
                  <Th
                    style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '16px',
                      border: 'none',
                      borderBottom: '1px solid #D1D3D4',
                      textAlign: 'left',
                      color: '#111827',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Admission Number
                  </Th>
                  <Th
                    style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '16px',
                      border: 'none',
                      borderBottom: '1px solid #D1D3D4',
                      textAlign: 'left',
                      color: '#111827',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Name
                  </Th>
                  <Th
                    style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '16px',
                      border: 'none',
                      borderBottom: '1px solid #D1D3D4',
                      textAlign: 'left',
                      color: '#111827',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Father's Name
                  </Th>
                  <Th
                    style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '16px',
                      border: 'none',
                      borderBottom: '1px solid #D1D3D4',
                      textAlign: 'left',
                      color: '#111827',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Email
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td
                      colSpan="5"
                      className="text-center"
                      style={{
                        padding: '40px',
                        fontSize: '14px',
                        color: '#6b7280',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Loading students...
                    </Td>
                  </Tr>
                ) : filteredStudents.length > 0 ? (
                  filteredStudents.map((student, index) => (
                    <Tr
                      key={student.id}
                      style={{
                        backgroundColor:
                          index % 2 === 0
                            ? '#ffffff'
                            : 'rgba(243, 244, 246, 0.4)',
                        transition: 'background-color 0.2s ease',
                        borderBottom: '1px solid #E5E7EB',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          'rgba(37, 99, 235, 0.06)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          index % 2 === 0
                            ? '#ffffff'
                            : 'rgba(243, 244, 246, 0.4)'
                      }}
                    >
                      <Td
                        style={{
                          fontSize: '14px',
                          padding: '16px',
                          border: 'none',
                          textAlign: 'left',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => handleStudentToggle(student.id)}
                        />
                      </Td>
                      <Td
                        style={{
                          fontSize: '14px',
                          padding: '16px',
                          border: 'none',
                          color: '#111827',
                          textAlign: 'left',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        {student.admission_number || 'N/A'}
                      </Td>
                      <Td
                        style={{
                          fontSize: '14px',
                          padding: '16px',
                          border: 'none',
                          color: '#111827',
                          textAlign: 'left',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        {student.name || 'N/A'}
                      </Td>
                      <Td
                        style={{
                          fontSize: '14px',
                          padding: '16px',
                          border: 'none',
                          color: '#111827',
                          textAlign: 'left',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        {student.father_name || 'N/A'}
                      </Td>
                      <Td
                        style={{
                          fontSize: '14px',
                          padding: '16px',
                          border: 'none',
                          color: '#111827',
                          textAlign: 'left',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        {student.email || 'N/A'}
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td
                      colSpan="5"
                      className="text-center"
                      style={{
                        padding: '40px',
                        fontSize: '14px',
                        color: '#6b7280',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      No students found
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </SuperTable>
          </Card>

          {/* Selected Count */}
          {selectedStudents.length > 0 && (
            <div
              className="mt-3"
              style={{
                padding: '12px 16px',
                backgroundColor: '#EFF6FF',
                borderRadius: '8px',
                border: '1px solid #223843',
              }}
            >
              <p
                className="m-0"
                style={{
                  fontSize: '14px',
                  color: '#223843',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontWeight: '500',
                }}
              >
                <BsCheckCircleFill className="me-2" />
                {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={Boolean(previewTemplateId)}
        onHide={() => setPreviewTemplateId(null)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px' }}>
            {previewTemplate?.title || 'Template Preview'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="template-preview-wrap">
            {PreviewComponent ? (
              <PreviewComponent
                studentName="Student Name"
                courseName="Course Name"
                date={new Date().toLocaleDateString()}
                instituteName={name || 'Institute Name'}
                certificateTitle="Transfer Certificate"
                authorityName="Authorized Signatory"
                certificateId="TC-0001"
                instructorName="Principal"
                watermarkUrl="/logo.svg"
                logoUrl={logo}
                suppressSidebarOffset
              />
            ) : null}
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default AssignTemplateCertificate

